/**
 * @file timeFieldNav.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description Arrow keys between a planning board's time fields (Cam,
 *              2026-10-12): ↑ / ↓ go to the same field in the row above or
 *              below; ← at the start of a field and → at its end go to the
 *              field beside it in the row (the last field of the row above /
 *              the first of the row below at either end). Leaving a field
 *              blurs it, which saves it as it always did.
 *
 *              A field takes part by carrying data-time-row (its row) and
 *              data-time-col (0, 1, …) inside an element marked
 *              data-time-grid. Disabled fields are stepped over.
 */

/** The time fields of the grid around `el`, in screen order. */
function fieldsOf(el) {
  const grid = el.closest('[data-time-grid]');
  if (!grid) return [];
  // Collapsed sections are not rendered, so every field here can take focus
  // unless it is disabled.
  return [...grid.querySelectorAll('input[data-time-col]')].filter(i => !i.disabled);
}

/**
 * Handle an arrow key in a time field. Returns true (and has moved the focus)
 * when it did something; the caller then does nothing more with the key.
 */
export function timeFieldKeyNav(e) {
  const el = e.currentTarget || e.target;
  if (!el || !el.dataset || el.dataset.timeCol == null) return false;
  const key = e.key;
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return false;
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return false;
  const len = String(el.value || '').length;
  if (key === 'ArrowLeft' && !(el.selectionStart === 0 && el.selectionEnd === 0)) return false;
  if (key === 'ArrowRight' && !(el.selectionStart === len && el.selectionEnd === len)) return false;

  const fields = fieldsOf(el);
  const col = el.dataset.timeCol;
  const row = el.dataset.timeRow;
  let target = null;
  if (key === 'ArrowUp' || key === 'ArrowDown') {
    const sameCol = fields.filter(f => f.dataset.timeCol === col);
    const i = sameCol.indexOf(el);
    target = sameCol[i + (key === 'ArrowUp' ? -1 : 1)] || null;
  } else {
    const i = fields.indexOf(el);
    target = fields[i + (key === 'ArrowLeft' ? -1 : 1)] || null;
    if (target && target.dataset.timeRow === row && target.dataset.timeCol === col) target = null;
  }
  if (!target) return false;
  e.preventDefault();
  e.stopPropagation();
  target.focus();
  return true;
}
